import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { TableTitleExtraClass } from 'components/TableTitleExtraClass';
import type { CatalogOtherCostVariableItemData } from 'gql/graphql';
import { CatalogOtherCostVariableTypeEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { TableColumn } from 'uikit';
import { Button, IconEdit, IconTrash, IconEye } from 'uikit';

import { useCallback, useMemo } from 'react';

import type { VariableIdType } from '../../types';
import Styles from '../catalogTable.module.css';

interface IUseVariablesColumns {
    onEdit: (id: VariableIdType) => () => void;
    onView: (id: number) => () => void;
    onRemove: (id: Exclude<VariableIdType, undefined>) => () => void;
    catalogsRules?: IUseAccess['catalogs'];
    showSubsidiaryName?: boolean;
}

export const useCommonVariablesColumns = ({
    onEdit,
    onView,
    onRemove,
    catalogsRules,
    showSubsidiaryName = false,
}: IUseVariablesColumns) => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const handleEdit = useCallback(
        (id) => () => {
            onEdit(id)();

            sendGpnAnalyticsEvent({
                componentId: 'rim_directory window choose ticker_pressed',
            });
        },
        [onEdit, sendGpnAnalyticsEvent],
    );

    return useMemo<TableColumn<CatalogOtherCostVariableItemData>[]>(
        () => [
            {
                accessor: 'id',
                title: null,
                width: 76,
                // eslint-disable-next-line react/display-name
                renderCell: ({ id, canDelete, name }) => {
                    const isEditable = canDelete && catalogsRules?.canEdit;

                    return (
                        <div className={Styles.actionsColumn}>
                            {isEditable ? (
                                <Button
                                    onClick={handleEdit(id)}
                                    onlyIcon
                                    iconRight={IconEdit}
                                    size="s"
                                    view="clear"
                                    disabled={!canDelete || !catalogsRules?.canEdit}
                                />
                            ) : (
                                <Button onClick={onView?.(id)} onlyIcon iconRight={IconEye} size="s" view="clear" />
                            )}
                            <ComponentWithTooltip
                                direction="upRight"
                                tooltipText={
                                    canDelete
                                        ? ''
                                        : 'Переменная включена в состав формулы прочих затрат. Удаление переменной невозможно.'
                                }
                            >
                                <Button
                                    onClick={onRemove(id)}
                                    onlyIcon
                                    iconRight={IconTrash}
                                    size="s"
                                    view="clear"
                                    disabled={!canDelete || !catalogsRules?.canDelete}
                                />
                            </ComponentWithTooltip>
                        </div>
                    );
                },
                withoutPadding: true,
            },
            {
                accessor: 'name',
                title: (
                    <TableTitleExtraClass className="name" classNameExtra="addBorder_l">
                        Наименование переменной
                    </TableTitleExtraClass>
                ),
                width: 800,
                sortable: true,
            },
            {
                accessor: 'groupName',
                title: (
                    <TableTitleExtraClass className="groupName" classNameExtra="addBorder_l">
                        Группа переменных
                    </TableTitleExtraClass>
                ),
                width: 235,
                sortable: true,
            },
            ...(showSubsidiaryName
                ? [
                      {
                          accessor: 'subsidiaryName' as const,
                          title: (
                              <TableTitleExtraClass className="subsidiaryName" classNameExtra="addBorder_l">
                                  Дочернее общество
                              </TableTitleExtraClass>
                          ),
                          width: 280,
                          sortable: true,
                      },
                  ]
                : []),
            {
                accessor: 'dataSourceName',
                title: (
                    <TableTitleExtraClass className="formula" classNameExtra="addBorder_l">
                        Источник данных / Формула расчета
                    </TableTitleExtraClass>
                ),
                renderCell: ({ type, dataSourceName, formula, otherCostType, limitedCostType }) => {
                    if (type === CatalogOtherCostVariableTypeEnum.Simple) {
                        return dataSourceName;
                    }
                    if (type === CatalogOtherCostVariableTypeEnum.Composite) {
                        return formula;
                    }

                    return otherCostType?.name ?? limitedCostType?.name;
                },
            },
        ],
        [catalogsRules?.canDelete, catalogsRules?.canEdit, handleEdit, onRemove, onView, showSubsidiaryName],
    );
};
